import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { setPremium as setLocalPremium } from "@/components/PremiumGate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

// Sync the user's server-side is_premium / role with the local PremiumGate hook
// so all existing PremiumGate consumers (AiCoach, FoodLog) auto-unlock for owners
// without needing to refactor every component.
// For non-owner/non-premium users, REVOKE any stale local "Demo Unlock" flag —
// this plugs the legacy free-premium leak.
function syncLocalPremium(user) {
  if (user && (user.role === "owner" || user.is_premium)) {
    setLocalPremium(true);
  } else if (user) {
    setLocalPremium(false);
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauthed, object = authed
  const [loading, setLoading] = useState(true);
  const badgeCatalog = useRef(null);

  // Lazy-load badge catalog once for unlock toasts
  const getBadgeCatalog = useCallback(async () => {
    if (badgeCatalog.current) return badgeCatalog.current;
    try {
      const { data } = await axios.get(`${API}/badges/all`);
      badgeCatalog.current = Object.fromEntries((data || []).map((b) => [b.id, b]));
      return badgeCatalog.current;
    } catch {
      return {};
    }
  }, []);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
      syncLocalPremium(data);
    } catch (e) {
      // Access token expired/missing — try silent refresh BEFORE giving up.
      // /auth/refresh swaps the refresh_token cookie for a fresh access token.
      try {
        const r = await axios.post(`${API}/auth/refresh`);
        setUser(r.data.user);
        syncLocalPremium(r.data.user);
        // First visit of the PT day → fire the daily welcome toast.
        if (r.data.first_visit_today) showDailyWelcome(r.data.user);
      } catch {
        setUser(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Daily welcome toast — fires once per Pacific Time date on silent re-auth.
  // Shows the user's name + current sign-in streak so it feels personal.
  function showDailyWelcome(u) {
    if (!u) return;
    const name = u.name || "Phoenix";
    const streak = u.signin_streak || u.streak || 0;
    const streakText = streak > 0 ? `Day ${streak} 🔥 of your streak` : "Welcome back!";
    toast.success(`Welcome back, ${name}!`, {
      description: streakText,
      duration: 5000,
      style: {
        background: "linear-gradient(135deg, #1a0d2b 0%, #0F0F0F 100%)",
        border: "2px solid #D4AF37",
        color: "#fff",
      },
    });
  }

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password, rememberMe = true) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password, remember_me: rememberMe });
    setUser(data.user);
    syncLocalPremium(data.user);
    return data.user;
  };

  const loginWithPasskey = async () => {
    // 🔐 Face ID / passkey passwordless flow. Session cookies are set
    // server-side on /api/passkeys/login/verify — we just adopt the user.
    const { loginWithPasskey: doPasskeyLogin } = await import("../lib/passkeys");
    const data = await doPasskeyLogin();
    setUser(data.user);
    syncLocalPremium(data.user);
    return data.user;
  };

  const register = async (name, email, password, rememberMe = true) => {
    const { data } = await axios.post(`${API}/auth/register`, { name, email, password, remember_me: rememberMe });
    setUser(data.user);
    syncLocalPremium(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch {
      // ignore
    }
    setUser(false);
  };

  const refreshUser = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
      syncLocalPremium(data);
    } catch {
      // ignore
    }
  };

  const updateProfile = async (patch) => {
    const { data } = await axios.patch(`${API}/auth/profile`, patch);
    setUser(data);
    return data;
  };

  const togglePremium = async () => {
    const { data } = await axios.post(`${API}/auth/premium-toggle`);
    setUser(data);
    return data;
  };

  const logActivity = async (type) => {
    if (!user) return null;
    try {
      const { data } = await axios.post(`${API}/activity/log`, { type });
      setUser(data.user);
      // Celebrate new badges
      const newBadges = data?.new_badges || [];
      if (newBadges.length > 0) {
        const catalog = await getBadgeCatalog();
        newBadges.forEach((id, i) => {
          const b = catalog[id];
          if (!b) return;
          setTimeout(() => {
            toast.success(`${b.icon || "🏆"}  Badge Unlocked!`, {
              description: b.title,
              duration: 5000,
              style: {
                background: "linear-gradient(135deg, #1a0d2b 0%, #0F0F0F 100%)",
                border: "2px solid #D4AF37",
                color: "#fff",
              },
            });
          }, i * 700); // stagger if multiple unlock at once
        });
      }
      return data;
    } catch {
      return null;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthed: !!user,
        loading,
        login,
        loginWithPasskey,
        register,
        logout,
        refreshUser,
        updateProfile,
        togglePremium,
        logActivity,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
